import { ApiClient } from './';
import {
  AccountLookup,
  CategoriesLookup,
  CategoriesLookupReq,
  FoodRecoveryTypesReq,
  GeneratorAddresses,
  MergedLookup,
  SubCategoriesLookup,
  SubCategoriesLookupReq,
  TypesLookupReq,
} from './api-types/lookups';
import { ItemDescType, ItemType, ItemTypeActivitySearch } from '../types';
import { EFGTypesMeta } from './api-types/food-generators';
import { AddressLookup } from './api-types/generators';
import { AssetContainerType } from './api-types/assets';
import {
  ActivitiesLookupItemType,
  ActivitiesUsersLookupResponse,
  ActivityUsersLookup,
} from './api-types/assets';
import { cacheRequest } from '../utils/cacheRequest';

const cached = <T>() => cacheRequest<T>();

export class LookupsApi extends ApiClient {
  private cache = {
    generators: cached<ItemType[]>(),
    haulers: cached<ItemType[]>(),
    allHaulers: cached<ItemType[]>(),
    accounts: cached<AccountLookup[]>(),
    jurisdictions: cached<ItemType[]>(),
    agencies: cached<ItemType[]>(),
    users: cached<ItemType[]>(),
    assetTypes: cached<ItemType[]>(),
    accountTypes: cached<ItemType[]>(),
    locationTypes: cached<ItemType[]>(),
    assetContainerTypes: cached<AssetContainerType[]>(),
    relatedTypes: cached<ItemType[]>(),
    clientTypes: cached<ItemType[]>(),
    clientStates: cached<ItemType[]>(),
    collectionSystems: cached<ItemType[]>(),
    contactRelatedTypes: cached<ItemType[]>(),
    unitOfMeasure: cached<ItemType[]>(),
    serviceWasteType: cached<ItemType[]>(),
  };
  getAssetTypes() {
    return this.cache.assetTypes(
      [],
      () => this.requestGet<ItemType[]>('activities/types')
    );
  }

  getAccountTypes() {
    return this.cache.accountTypes(
      [],
      () => this.requestGet<ItemType[]>('generators/types')
    );
  }

  getLocationTypes() {
    return this.cache.locationTypes(
      [],
      () => this.requestGet<ItemType[]>('locations/types')
    );
  }

  getAccountsDependingToEFG(id: number | string, isEfg: boolean = false) {
    return this.requestGet<ItemType[]>(`generators/lookups`, {
      'q.location.id': id,
      'q.isEfg': isEfg,
    });
  }

  getAssetCategories({ type, accountId, generatorId }: CategoriesLookupReq) {
    return this.requestGet<CategoriesLookup[]>('activities/categories', {
      'q.type.id': type,
      'q.account.id': accountId,
      'q.generator.id': generatorId,
    });
  }

  getAssetSubCategories({ id, typeId, accountId, generatorId }: SubCategoriesLookupReq) {
    return this.requestGet<SubCategoriesLookup[]>(
      `activities/categories/${id}/sub-categories/lookups`,
      {
        'q.category.id': typeId,
        'q.account.id': accountId,
        'q.generator.id': generatorId,
      },
    );
  }

  getAssetContainerTypes() {
    return this.cache.assetContainerTypes(
      [],
      () => this.requestGet<AssetContainerType[]>(
        `activities/container-types/lookups`
      )
    );
  }

  getAssetsWaiverStatus() {
    return this.requestGet<ItemType[]>('activities/waiver-statuses/lookups', {
      limit: 100,
    });
  }

  getActivitiesStatus() {
    return this.requestGet<ActivitiesLookupItemType[]>(
      'activities/statuses/lookups',
      {
        limit: 100,
      },
    );
  }

  getActivitiesPriorities() {
    return this.requestGet<ActivitiesLookupItemType[]>(
      'activities/priorities/lookups',
      {
        limit: 100,
      },
    );
  }

  activitiesUsersLookup(filters: Partial<ActivityUsersLookup> = {}) {
    return this.requestGet<ActivitiesUsersLookupResponse>(
      'activities/users/lookups',
      {
        'q.relatedToType.id': filters['relatedToType.id'],
        'q.relatedTo.id': filters['relatedTo.id'],
        'q.name': filters['name'],
        'q.accessMode': 1,
        'q.limit': 10,
      },
    );
  }

  getRelatedTypes() {
    return this.cache.relatedTypes(
      [],
      () => this.requestGet<ItemType[]>('activities/related-entities/types')
    );
  }

  getRelatedLookups({ type, name }: TypesLookupReq) {
    return this.requestGet<ItemType[]>('activities/related-entities/lookups', {
      type,
      ...(!!name && { name: name }),
      limit: 100,
    });
  }

  getActivitySearch({ type, name }: TypesLookupReq) {
    return this.requestGet<ItemTypeActivitySearch[]>(
      'activities/related-entities/lookups',
      {
        type,
        ...(!!name && { name: name }),
        limit: 25,
      },
    );
  }

  getFoodRecoveriesTypes({ name }: FoodRecoveryTypesReq) {
    return this.requestGet<ItemDescType[]>('food-recoveries/types', {
      ...(!!name && { name }),
    });
  }

  getFoodWillingTypeLookups({ name }: FoodRecoveryTypesReq) {
    return this.requestGet<ItemType[]>('food-recoveries/food-willing-types', {
      ...(!!name && { name }),
    });
  }

  getAvailableAccountsLookups({ name }: FoodRecoveryTypesReq) {
    return this.requestGet<ItemType[]>('generators/available-parents', {
      'q.accountNumber': !!name ? name : null,
    });
  }

  getAddressLookups(address?: string) {
    return this.requestGeneratorsAddresses<GeneratorAddresses>(
      'AddressLookup/autocomplete',
      {
        ...(!!address && { address }),
      },
    );
  }

  getClientTypes() {
    return this.cache.clientTypes(
      [],
      () => this.requestGet<ItemType[]>('clients/types')
    );
  }

  getJurisdictions(name?: string, withBlank?: boolean) {
    return this.cache.jurisdictions(
      [name, withBlank],
      () => this.requestGet<ItemType[]>(
        'clients/jurisdictions/lookups',
        {
          ...(!!name && { name }),
          withBlank,
        }
      )
    );
  }

  getAllJurisdictions() {
    return this.requestGet<ItemType[]>(
      'clients/jurisdictions/all',
      {},
      { headers: this.headersProps },
    );
  }

  getClientStates() {
    return this.cache.clientStates(
      [],
      () => this.requestGet<ItemType[]>('clients/states')
    );
  }

  getClients(type: number) {
    return this.requestGet<ItemType[]>('clients/lookups', {
      'type.id': type,
    });
  }

  getClientsHaulers(name?: string) {
    return this.cache.haulers(
      [name],
      () => this.requestGet<ItemType[]>(
        'clients/haulers/lookups',
        {
          'q.name': !!name ? name : null,
          limit: 100,
        }
      )
    );
  }

  getAllHaulers(name?: string) {
    return this.cache.allHaulers(
      [name],
      () => this.requestGet<ItemType[]>(
        'clients/haulers/lookups/all',
        {
          name: !!name ? name : null,
        }
      )
    );
  }

  getGeneratorsLookups(name?: string) {
    return this.cache.generators(
      [name],
      () => this.requestGet<ItemType[]>(
        'locations/lookups',
        {
          'q.name': !!name ? name : null,
          limit: 100,
        }
      )
    );
  }

  getAccountsLookups(name?: string, locationId?: number | null) {
    return this.cache.accounts(
      [name, locationId],
      () => this.requestGet<AccountLookup[]>(
        'generators/lookups',
        {
          'q.accountNumber': !!name ? name : null,
          'q.location.id': !!locationId ? locationId : null,
          limit: 100,
        }
      )
    );
  }

  getTiersLookups(name?: string) {
    return this.requestGet<ItemType[]>('edible-food-generators/tiers', {
      ...(!!name && { name }),
    });
  }

  getEdibleFoodGeneratorsTypes(name?: string) {
    return this.requestGet<EFGTypesMeta[]>('edible-food-generators/types', {
      ...(!!name && { name }),
    });
  }

  getFoodRecoveriesLookups(
    name?: string,
    id?: number | string,
    jurisdictionId?: number | string,
  ) {
    return this.requestGet<ItemType[]>('food-recoveries/lookups', {
      ...(!!name && { name }),
      ...(!!id && { 'type.id': id }),
      ...(!!jurisdictionId && { 'jurisdiction.id': jurisdictionId }),
    });
  }

  getWasteMaterialTypes(name?: string) {
    return this.requestGet<ItemType[]>('waste-material-types/lookups', {
      ...(!!name && { name }),
    });
  }

  getJurisdictionZones(name?: string) {
    return this.requestGet<ItemType[]>('locations/zones', {
      ...(!!name && { name }),
    });
  }

  getUsers(name?: string) {
    return this.cache.users(
      [name],
      () => this.requestGet<ItemType[]>(
        'users/lookups',
        {
          ...(!!name && { name }),
        }
      )
    );
  }

  getCollectionSystems() {
    return this.cache.collectionSystems(
      [],
      () => this.requestGet<ItemType[]>('compliance-matrix/collection-systems')
    );
  }

  getExtensionField(
    fieldNumber: number,
    jurisdictionId: number,
    name?: string,
  ) {
    return this.requestGet<ItemType[]>(
      `generators/extension-fields-${fieldNumber}`,
      {
        jurisdictionId: jurisdictionId,
        ...(!!name && { name }),
      },
    );
  }

  getContactTypes(name?: string) {
    return this.requestGet<ItemType[]>('contacts/types/lookups', {
      ...(!!name && { name }),
    });
  }

  getGeneratorsAddresses(name?: string, jurisdictionId?: number) {
    return this.requestGet<AddressLookup[]>('locations/lookups/with-address', {
      ...(!!name && { 'q.name': name }),
      ...(!!jurisdictionId && { 'q.jurisdiction.id': jurisdictionId }),
    });
  }

  getSearchGeneratorsAddresses(generatorId: string | number, value: any) {
    return this.requestGet<AddressLookup[]>(
      `generators/${generatorId}/locations`,
      {
        ...value,
      },
    );
  }

  getAgenciesLookup(name?: string, accessType?: string, withBlank?: boolean) {
    return this.cache.agencies(
      [name, accessType, withBlank],
      () => this.requestGet<ItemType[]>(
        'clients/agencies/lookups',
        {
          ...(!!name && { name }),
          ...(!!accessType && { accessType }),
          withBlank,
        }
      )
    );
  }

  getContactRelatedTypes() {
    return this.cache.contactRelatedTypes(
      [],
      () => this.requestGet<ItemType[]>('contacts/related-entities/types')
    );
  }

  getUnitOfMeasure() {
    return this.cache.unitOfMeasure(
      [],
      () => this.requestGet<ItemType[]>('services/units-of-measure')
    );
  }

  getServiceWasteType() {
    return this.cache.serviceWasteType(
      [],
      () => this.requestGet<ItemType[]>('services/waste-types')
    );
  }

  getMergedSubCategories(withOutBlank?: boolean) {
    return this.requestGet<MergedLookup[]>(
      'activities/sub-categories/lookups/merged',
      {
        withBlank: withOutBlank ? false : true,
      },
    );
  }

  getMergedZones(withOutBlank?: boolean) {
    return this.requestGet<MergedLookup[]>('locations/zones/lookups/merged', {
      withBlank: withOutBlank ? false : true,
    });
  }

  getMergedExtensionField1(withOutBlank?: boolean) {
    return this.requestGet<MergedLookup[]>(
      'generators/extension-fields-1/lookups/merged',
      {
        withBlank: withOutBlank ? false : true,
      },
    );
  }

  getMergedExtensionField2(withOutBlank?: boolean) {
    return this.requestGet<MergedLookup[]>(
      'generators/extension-fields-2/lookups/merged',
      {
        withBlank: withOutBlank ? false : true,
      },
    );
  }
}
